import React, { useState } from 'react';
import { Modal, Input, message, Alert } from 'antd';
import { useChangeMyPassword, MIN_PASSWORD_LENGTH } from '../hooks/useAccountSecurity';

/**
 * เปลี่ยนรหัสผ่านของตัวเอง — เปิดจากเมนูโปรไฟล์มุมขวาบน
 *
 * ยืนยันด้วยรหัส 6 หลักจาก Google Authenticator แทนการถามรหัสผ่านเดิม
 * เหตุผล: ถ้าใช้รหัสเดิมยืนยัน คนที่แอบใช้เครื่องที่เปิดค้างไว้ก็เปลี่ยนรหัสได้
 * แต่ถ้าใช้ 2FA ต้องมีมือถือของเจ้าตัวจริง ๆ
 *
 * คนที่ admin ปิด 2FA ไว้จะไม่มีรหัส 6 หลัก → backend จะตอบว่าให้ติดต่อ admin
 */
const ChangePasswordModal = ({ open, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpToken, setTotpToken] = useState('');
  const { mutateAsync: changePassword, isLoading } = useChangeMyPassword();

  const reset = () => {
    setNewPassword('');
    setConfirmPassword('');
    setTotpToken('');
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async () => {
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      message.warning(`รหัสผ่านต้องยาวอย่างน้อย ${MIN_PASSWORD_LENGTH} ตัวอักษร`);
      return;
    }
    if (newPassword !== confirmPassword) {
      message.warning('รหัสผ่านทั้งสองช่องไม่ตรงกัน');
      return;
    }
    if (!/^\d{6}$/.test(totpToken)) {
      message.warning('กรอกรหัส 6 หลักจากแอป Google Authenticator');
      return;
    }
    try {
      await changePassword({ newPassword, totpToken });
      message.success('เปลี่ยนรหัสผ่านสำเร็จ — ครั้งถัดไปให้ใช้รหัสใหม่');
      handleClose();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'เปลี่ยนรหัสผ่านไม่สำเร็จ');
    }
  };

  return (
    <Modal
      title="เปลี่ยนรหัสผ่าน"
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="เปลี่ยนรหัสผ่าน"
      cancelText="ยกเลิก"
      confirmLoading={isLoading}
      okButtonProps={{ disabled: isLoading }}
      maskClosable={false}
      destroyOnClose
      zIndex={2000}
    >
      <div className="flex flex-col gap-3">
        <div>
          <div className="text-sm text-gray-600 mb-1">รหัสผ่านใหม่</div>
          <Input.Password
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={`อย่างน้อย ${MIN_PASSWORD_LENGTH} ตัวอักษร`}
            autoFocus
          />
        </div>
        <div>
          <div className="text-sm text-gray-600 mb-1">ยืนยันรหัสผ่านใหม่</div>
          <Input.Password
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="พิมพ์ซ้ำอีกครั้ง"
          />
        </div>
        <div>
          <div className="text-sm text-gray-600 mb-1">รหัส 6 หลักจาก Google Authenticator</div>
          <Input
            value={totpToken}
            onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onPressEnter={handleSubmit}
            placeholder="000000"
            maxLength={6}
            inputMode="numeric"
            style={{ textAlign: 'center', fontSize: 22, letterSpacing: 6 }}
          />
        </div>
        <Alert
          type="info"
          showIcon
          message="ใช้รหัสจากแอปยืนยันแทนรหัสผ่านเดิม ถ้าเปิดแอปไม่ได้ให้ติดต่อผู้ดูแลระบบ"
        />
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
